/**
 * @file Definition data-driven des 10 modes de jeu (regles de controle, cibles, timer, victoire/defaite, spawn, params).
 */
import { HUB_RADIUS } from './hub';
import type { ModeId } from '@shared/enums';
import type { ControlRules, ModeDef, ModeRules } from './types';

/** Controles de combat : AutoMove et AutoFire suivent le reglage joueur (ON par defaut). */
const COMBAT: ControlRules = { canMove: true, canFire: true, autoMove: 'player', autoFire: 'player' };
/** Hub social : deplacement 100% manuel, aucun tir (valeurs forcees, non modifiables par le joueur). */
const SOCIAL: ControlRules = { canMove: true, canFire: false, autoMove: 'forceOff', autoFire: 'forceOff' };

const rules = (r: Partial<ModeRules> & Pick<ModeRules, 'spawn' | 'arena'>): ModeRules => ({
  control: COMBAT,
  targetableFamilies: ['tree', 'rock', 'ore', 'monster', 'boss'],
  timer: { kind: 'none', seconds: 0 },
  win: { kind: 'never' },
  lose: { kind: 'never' },
  killRewards: 'money',
  hud: 'combat',
  cameraFollow: false,
  ...r,
});

export const MODES: { [K in ModeId]: ModeDef<K> } = {
  // ------------------------------------------------------------------ Main : abattre des arbres, stage par stage
  main: {
    id: 'main',
    name: { fr: 'Foret des chats', en: 'Cat Woods' },
    impl: 'main',
    status: 'playable',
    entry: { unlock: { kind: 'always' } },
    rules: rules({
      targetableFamilies: ['tree'],
      // Comme l'original : une quantite fixe d'arbres par niveau (ils ne repoussent pas), chrono de 5 min (stage.levelSeconds).
      timer: { kind: 'countdown', seconds: 300 },
      win: { kind: 'killAll' },
      lose: { kind: 'timerExpired' },
      // Chemin en oblique (comme l'original) : couloir le long de +y, camera qui suit le chef, lacet 45 deg.
      arena: { shape: 'corridor', halfW: 8, halfH: 4 },
      cameraFollow: true,
      cameraYawDeg: 45,
      // Vue plus rapprochee que la valeur par defaut (24) : on profite des chats. 17 = couloir (16 u) + un liseré de falaise.
      cameraFitWidth: 17,
      spawn: { table: 'biome', layout: 'scatter', initialCount: 40, respawn: 'none', maxAlive: 110 },
    }),
    params: { challengeAuto: true, failFallbackLevels: 0 },
  },
  // ------------------------------------------------------------------ Monster : defense de vagues autour d'une barriere
  monster: {
    id: 'monster',
    name: { fr: 'Defense des monstres', en: 'Monster Defense' },
    impl: 'monster',
    status: 'playable',
    entry: { unlock: { kind: 'stageValue', atLeast: 20 } },
    rules: rules({
      targetableFamilies: ['monster', 'boss'],
      killRewards: 'money',
      win: { kind: 'surviveWaves', waves: 30 },
      lose: { kind: 'barrierBroken' },
      arena: { shape: 'circle', halfW: 13, halfH: 13 },
      cameraFitWidth: 30,
      spawn: {
        table: [
          { targetId: 'mon_blob', weight: 100 },
          { targetId: 'mon_runner', weight: 40, minValue: 5 },
          { targetId: 'mon_bomber', weight: 25, minValue: 15 },
          { targetId: 'mon_tank', weight: 15, minValue: 25 },
        ],
        layout: 'ring',
        initialCount: 0,
        respawn: 'interval',
        respawnIntervalSeconds: 0.8,
        maxAlive: 60,
      },
    }),
    params: {
      barrierRadius: 5.5,
      barrierHp: 20,
      waveSeconds: 20,
      wavesPerRun: 30,
      monstersPerWaveBase: 8,
      monstersPerWaveGrowth: 1.15,
      bossEveryWaves: 10,
      knockback: 1.2,
      hpPerWaveGrowth: 1.12,
      petCoinPerWave: 2,
    },
  },
  // ------------------------------------------------------------------ SpeedRun (squelette)
  speedRun: {
    id: 'speedRun',
    name: { fr: 'Course contre la montre', en: 'Speed Run' },
    impl: 'speedRun',
    status: 'skeleton',
    entry: { unlock: { kind: 'stageValue', atLeast: 50 }, ticket: 'speedRun', sweep: true },
    rules: rules({
      targetableFamilies: ['tree'],
      timer: { kind: 'countdown', seconds: 45 },
      win: { kind: 'timerEnd' },
      lose: { kind: 'never' },
      arena: { shape: 'circle', halfW: 11, halfH: 11 },
      spawn: { table: [{ targetId: 'tree_speedrun', weight: 1 }], layout: 'ring', initialCount: 24, respawn: 'onKill', maxAlive: 24 },
    }),
    params: { treeCount: 24, ringRadius: 9, moneyMult: 2 },
  },
  // ------------------------------------------------------------------ Mine : minerais (squelette)
  mine: {
    id: 'mine',
    name: { fr: 'Mine', en: 'Mine' },
    impl: 'mine',
    status: 'skeleton',
    entry: { unlock: { kind: 'stageValue', atLeast: 60 }, ticket: 'mine', sweep: true },
    rules: rules({
      targetableFamilies: ['ore', 'rock'],
      killRewards: 'resources',
      timer: { kind: 'countdown', seconds: 60 },
      win: { kind: 'timerEnd' },
      arena: { shape: 'rect', halfW: 14, halfH: 20 },
      cameraFollow: true,
      spawn: {
        table: [
          { targetId: 'ore_iron', weight: 100 },
          { targetId: 'ore_copper', weight: 70 },
          { targetId: 'ore_silver', weight: 30 },
          { targetId: 'ore_gold', weight: 10 },
          { targetId: 'ore_crystal', weight: 3 },
          { targetId: 'rock_small', weight: 40 },
        ],
        layout: 'noise',
        initialCount: 30,
        respawn: 'none',
        maxAlive: 40,
      },
    }),
    params: { oreCount: 30, noiseScale: 0.18, gemPerOre: 1, ticketReward: 0 },
  },
  // ------------------------------------------------------------------ Boss (squelette)
  boss: {
    id: 'boss',
    name: { fr: 'Boss errant', en: 'Wandering Boss' },
    impl: 'boss',
    status: 'playable',
    entry: { unlock: { kind: 'stageValue', atLeast: 70 }, ticket: 'boss', sweep: true },
    rules: rules({
      targetableFamilies: ['boss'],
      timer: { kind: 'countdown', seconds: 90 },
      win: { kind: 'killBoss' },
      lose: { kind: 'timerExpired' },
      arena: { shape: 'circle', halfW: 14, halfH: 14 },
      spawn: { table: [{ targetId: 'boss_golem', weight: 1 }], layout: 'center', initialCount: 1, respawn: 'none', maxAlive: 1 },
    }),
    params: {
      mapRadius: 14,
      bossHpMult: 40,
      wanderSpeed: 1.8,
      levelHpGrowth: 1.22,
      maxScaleLevel: 40,
      phaseAt: [0.66, 0.33],
      pylonCount: [3, 5],
      pylonHpMult: 3,
      phaseSpeedMult: [1, 1.4, 1.9],
      chargeEvery: [6, 4.5, 3],
    },
  },
  // ------------------------------------------------------------------ Raid (squelette)
  raid: {
    id: 'raid',
    name: { fr: 'Raid', en: 'Raid' },
    impl: 'raid',
    status: 'playable',
    entry: { unlock: { kind: 'stageValue', atLeast: 100 }, ticket: 'raid' },
    rules: rules({
      targetableFamilies: ['boss'],
      killRewards: 'score',
      timer: { kind: 'countdown', seconds: 90 },
      win: { kind: 'killBoss' },
      lose: { kind: 'timerExpired' },
      arena: { shape: 'circle', halfW: 14, halfH: 14 },
      spawn: { table: [{ targetId: 'raid_dragon', weight: 1 }], layout: 'center', initialCount: 1, respawn: 'none', maxAlive: 1 },
    }),
    params: {
      bossHpPerLevel: 1.8,
      seasonDays: 14,
      freeEntries: 2,
      helperCat: true,
      starGemPerTier: 10,
      maxScaleLevel: 60,
      phaseAt: [0.66, 0.33],
      phaseSpeedMult: [1, 1.25, 1.7],
      eggEvery: [0, 7, 4.5],
      eggCount: [0, 2, 3],
      eggHealPct: [0, 0.04, 0.06],
      eggLifeSeconds: 6,
      eggHpMult: 1.5,
    },
  },
  // ------------------------------------------------------------------ Adventure : expeditions de familiers (squelette, sans combat)
  adventure: {
    id: 'adventure',
    name: { fr: 'Aventure', en: 'Adventure' },
    impl: 'adventure',
    status: 'skeleton',
    entry: { unlock: { kind: 'stageValue', atLeast: 120 }, ticket: 'adventure' },
    rules: rules({
      control: { canMove: false, canFire: false, autoMove: 'forceOff', autoFire: 'forceOff' },
      targetableFamilies: [],
      killRewards: 'none',
      hud: 'minimal',
      arena: { shape: 'rect', halfW: 11, halfH: 15 },
      spawn: { table: [], layout: 'none', initialCount: 0, respawn: 'none', maxAlive: 0 },
    }),
    params: { mapCount: 3, expeditionHours: 4 },
  },
  // ------------------------------------------------------------------ Fish : aquarium passif (squelette, sans combat)
  fish: {
    id: 'fish',
    name: { fr: 'Aquarium', en: 'Aquarium' },
    impl: 'fish',
    status: 'skeleton',
    entry: { unlock: { kind: 'stageValue', atLeast: 140 } },
    rules: rules({
      control: { canMove: false, canFire: false, autoMove: 'forceOff', autoFire: 'forceOff' },
      targetableFamilies: [],
      killRewards: 'none',
      hud: 'minimal',
      arena: { shape: 'rect', halfW: 11, halfH: 15 },
      spawn: { table: [], layout: 'none', initialCount: 0, respawn: 'none', maxAlive: 0 },
    }),
    params: { slots: 20, maxFish: 20 },
  },
  // ------------------------------------------------------------------ Harvest : recolte de ressources (squelette jouable)
  harvest: {
    id: 'harvest',
    name: { fr: 'Recolte', en: 'Harvest' },
    impl: 'harvest',
    status: 'skeleton',
    entry: { unlock: { kind: 'stageValue', atLeast: 80 } },
    rules: rules({
      targetableFamilies: ['tree', 'rock', 'ore'],
      killRewards: 'resources',
      arena: { shape: 'rect', halfW: 13, halfH: 18 },
      cameraFollow: true,
      spawn: {
        table: [
          { targetId: 'tree_oak', weight: 50 },
          { targetId: 'tree_fir', weight: 30 },
          { targetId: 'rock_small', weight: 30 },
          { targetId: 'rock_big', weight: 12 },
          { targetId: 'ore_iron', weight: 15 },
          { targetId: 'ore_copper', weight: 10 },
          { targetId: 'ore_silver', weight: 4 },
          { targetId: 'ore_gold', weight: 2 },
        ],
        layout: 'scatter',
        initialCount: 30,
        respawn: 'interval',
        respawnIntervalSeconds: 6,
        maxAlive: 40,
      },
    }),
    params: { nodeCount: 30, respawnSeconds: 6 },
  },
  // ------------------------------------------------------------------ Hub social (squelette : AUCUN tir, AUCUN auto)
  hub: {
    id: 'hub',
    name: { fr: 'Place des chats', en: 'Cat Plaza' },
    impl: 'hub',
    status: 'skeleton',
    entry: { unlock: { kind: 'always' } },
    rules: rules({
      control: SOCIAL,
      targetableFamilies: [],
      // Les decors du hub sont explicitement non tirables, quelle que soit leur definition.
      shootableOverride: { tree: false, rock: false, ore: false, monster: false, boss: false, decor: false },
      killRewards: 'none',
      hud: 'hub',
      cameraFollow: true,
      arena: { shape: 'circle', halfW: HUB_RADIUS, halfH: HUB_RADIUS },
      spawn: { table: [{ targetId: 'decor_tree', weight: 3 }, { targetId: 'decor_rock', weight: 1 }, { targetId: 'decor_bush', weight: 3 }], layout: 'scatter', initialCount: 70, respawn: 'none', maxAlive: 110 },
    }),
    params: { maxPlayersPerInstance: 40 },
  },
};

export const MODE_LIST: readonly ModeDef[] = Object.values(MODES) as ModeDef[];
export const getModeDef = <M extends ModeId>(id: M): ModeDef<M> => MODES[id];
